/**
 * Client-side UX telemetry — batches lazy-section render measurements
 * and Web Vitals, then flushes them to the `ingest-ux-metrics` edge
 * function via `sendBeacon`. Zero PII; opt-out is via cookie consent.
 */

const SESSION_KEY = 'ux_metrics_session_id';
const FLUSH_INTERVAL_MS = 15_000;
const MAX_BUFFER = 40;

export type SectionEvent = {
  kind: 'section';
  section: string;
  route: string;
  language: string | null;
  skeleton_ms: number | null;
  chunk_ms: number | null;
  render_ms: number | null;
  stuck: boolean;
  errored: boolean;
  retries: number;
};

export type VitalEvent = {
  kind: 'vital';
  route: string;
  language: string | null;
  vital_name: 'LCP' | 'CLS' | 'INP' | 'FCP' | 'TTFB';
  vital_value: number;
};

type AnyEvent = SectionEvent | VitalEvent;

let buffer: AnyEvent[] = [];
let flushTimer: number | null = null;
let installed = false;

function sessionId(): string {
  try {
    let id = sessionStorage.getItem(SESSION_KEY);
    if (!id) {
      id =
        typeof crypto !== 'undefined' && 'randomUUID' in crypto
          ? crypto.randomUUID()
          : Math.random().toString(36).slice(2) + Date.now().toString(36);
      sessionStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return 'anon';
  }
}

function analyticsAllowed(): boolean {
  try {
    // Aligns with existing cookie-consent gating (see CookieConsent.tsx).
    // If no consent stored yet, default to sending anonymous perf beacons
    // (no cookies, no PII) — same posture as our cookieless GA4 setup.
    const raw = localStorage.getItem('cookie-consent');
    if (!raw) return true;
    const parsed = JSON.parse(raw) as { analytics?: boolean };
    return parsed?.analytics !== false;
  } catch {
    return true;
  }
}

function endpointUrl(): string | null {
  const base = (import.meta as unknown as { env: Record<string, string> }).env
    .VITE_SUPABASE_URL;
  if (!base) return null;
  return `${base.replace(/\/$/, '')}/functions/v1/ingest-ux-metrics`;
}

function envelope() {
  const nav =
    typeof navigator !== 'undefined'
      ? (navigator as Navigator & {
          connection?: { effectiveType?: string };
        })
      : undefined;
  return {
    session_id: sessionId(),
    ua: nav?.userAgent?.slice(0, 200) ?? null,
    viewport_w:
      typeof window !== 'undefined' ? Math.round(window.innerWidth) : null,
    connection: nav?.connection?.effectiveType ?? null,
    events: buffer,
  };
}

function doFlush(useBeacon: boolean) {
  if (buffer.length === 0) return;
  const url = endpointUrl();
  if (!url) {
    buffer = [];
    return;
  }
  const payload = JSON.stringify(envelope());
  buffer = [];
  try {
    if (useBeacon && 'sendBeacon' in navigator) {
      navigator.sendBeacon(
        url,
        new Blob([payload], { type: 'application/json' })
      );
      return;
    }
    void fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: payload,
      keepalive: true,
    }).catch(() => {
      /* fire-and-forget */
    });
  } catch {
    /* ignore */
  }
}

function scheduleFlush() {
  if (flushTimer != null) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    doFlush(false);
  }, FLUSH_INTERVAL_MS);
}

function installOnce() {
  if (installed || typeof window === 'undefined') return;
  installed = true;
  const flushNow = () => doFlush(true);
  window.addEventListener('pagehide', flushNow);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flushNow();
  });
}

function push(ev: AnyEvent) {
  if (typeof window === 'undefined') return;
  if (!analyticsAllowed()) return;
  installOnce();
  buffer.push(ev);
  if (buffer.length >= MAX_BUFFER) {
    doFlush(false);
    return;
  }
  scheduleFlush();
}

export function recordSection(
  ev: Omit<SectionEvent, 'kind' | 'route' | 'language'> & {
    route?: string;
    language?: string | null;
  }
) {
  push({
    kind: 'section',
    route:
      ev.route ??
      (typeof window !== 'undefined' ? window.location.pathname : ''),
    language:
      ev.language ??
      (typeof document !== 'undefined'
        ? document.documentElement.lang || null
        : null),
    section: ev.section,
    skeleton_ms: ev.skeleton_ms,
    chunk_ms: ev.chunk_ms,
    render_ms: ev.render_ms,
    stuck: ev.stuck,
    errored: ev.errored,
    retries: ev.retries,
  });
}

export function recordVital(
  name: VitalEvent['vital_name'],
  value: number
) {
  push({
    kind: 'vital',
    route: typeof window !== 'undefined' ? window.location.pathname : '',
    language:
      typeof document !== 'undefined'
        ? document.documentElement.lang || null
        : null,
    vital_name: name,
    vital_value: value,
  });
}

/**
 * Observe LCP, CLS, and INP via native PerformanceObserver — no extra
 * dependency needed. Fires once per page view; safe to call multiple
 * times because it dedupes via a module flag.
 */
let vitalsInstalled = false;
export function installWebVitals() {
  if (vitalsInstalled || typeof PerformanceObserver === 'undefined') return;
  vitalsInstalled = true;

  const safeObserve = (
    type: string,
    cb: (entry: PerformanceEntry) => void,
    opts: PerformanceObserverInit = {}
  ) => {
    try {
      const obs = new PerformanceObserver((list) => {
        for (const e of list.getEntries()) cb(e);
      });
      obs.observe({ type, buffered: true, ...opts });
    } catch {
      /* unsupported entry type in this browser */
    }
  };

  let lcp = 0;
  safeObserve('largest-contentful-paint', (e) => {
    lcp = Math.max(lcp, e.startTime);
  });

  let cls = 0;
  safeObserve('layout-shift', (e) => {
    const ls = e as PerformanceEntry & { value: number; hadRecentInput: boolean };
    if (!ls.hadRecentInput) cls += ls.value;
  });

  let inp = 0;
  safeObserve('event', (e) => {
    const ev = e as PerformanceEntry & { duration: number; interactionId?: number };
    if (ev.interactionId && ev.duration > inp) inp = ev.duration;
  }, { durationThreshold: 40 } as PerformanceObserverInit);

  const report = () => {
    if (lcp) recordVital('LCP', Math.round(lcp));
    if (cls) recordVital('CLS', Math.round(cls * 1000) / 1000);
    if (inp) recordVital('INP', Math.round(inp));
  };

  window.addEventListener('pagehide', report, { once: true });
  document.addEventListener(
    'visibilitychange',
    () => {
      if (document.visibilityState === 'hidden') report();
    },
    { once: true }
  );
}
